// Serviço para gerenciar produtos no Supabase
import { supabase } from '../lib/supabase';
import type { Produto } from '../types';

// Interface para produto no banco de dados
interface ProdutoDB {
  id: string;
  codigo: string;
  descricao: string;
  categoria: string;
  preco_venda: number;
  preco_compra: number;
  unidade_medida: string;
  ncm?: string;
  cest?: string;
  estoque?: number;
  imagem?: string;
  created_at: string;
  updated_at: string;
}

// Converter produto do banco para o formato da aplicação
function produtoDBToProduto(produtoDB: ProdutoDB): Produto {
  return {
    id: produtoDB.id,
    codigo: produtoDB.codigo,
    descricao: produtoDB.descricao,
    categoria: produtoDB.categoria,
    precoVenda: produtoDB.preco_venda,
    precoCompra: produtoDB.preco_compra,
    unidadeMedida: produtoDB.unidade_medida,
    ncm: produtoDB.ncm || undefined,
    cest: produtoDB.cest || undefined,
    estoque: produtoDB.estoque || undefined,
    imagem: produtoDB.imagem || undefined,
    createdAt: produtoDB.created_at,
    updatedAt: produtoDB.updated_at,
  };
}

// Converter produto da aplicação para o formato do banco
function produtoToProdutoDB(produto: Partial<Produto>): Partial<ProdutoDB> {
  return {
    codigo: produto.codigo,
    descricao: produto.descricao,
    categoria: produto.categoria,
    preco_venda: produto.precoVenda,
    preco_compra: produto.precoCompra,
    unidade_medida: produto.unidadeMedida,
    ncm: produto.ncm || null,
    cest: produto.cest || null,
    estoque: produto.estoque || null,
    imagem: produto.imagem || null,
  };
}

// Buscar todos os produtos da empresa atual
export async function getProdutos(empresaId: string): Promise<Produto[]> {
  try {
    const { data: produtos, error } = await supabase
      .from('produtos')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[produtosService] Erro ao buscar produtos:', error);
      // Retornar array vazio em vez de quebrar
      return [];
    }

    return (produtos || []).map(produtoDBToProduto);
  } catch (error) {
    console.error('[produtosService] Erro geral ao buscar produtos:', error);
    // Retornar array vazio em vez de quebrar
    return [];
  }
}

// Buscar produto por ID
export async function getProdutoById(id: string): Promise<Produto | null> {
  try {
    const { data: produto, error } = await supabase
      .from('produtos')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!produto) return null;

    return produtoDBToProduto(produto);
  } catch (error) {
    console.error('Erro ao buscar produto:', error);
    throw error;
  }
}

// Criar novo produto
export async function createProduto(
  produto: Omit<Produto, 'id' | 'createdAt' | 'updatedAt'>,
  contaId: string,
  empresaId: string
): Promise<Produto> {
  try {
    const produtoData = produtoToProdutoDB(produto);
    
    const { data: novoProduto, error } = await supabase
      .from('produtos')
      .insert({
        ...produtoData,
        conta_id: contaId,
        empresa_id: empresaId,
      })
      .select()
      .single();

    if (error) throw error;

    return produtoDBToProduto(novoProduto);
  } catch (error) {
    console.error('Erro ao criar produto:', error);
    throw error;
  }
}

// Atualizar produto
export async function updateProduto(id: string, produto: Partial<Produto>): Promise<Produto> {
  try {
    const produtoData = produtoToProdutoDB(produto);
    
    const { data: produtoAtualizado, error } = await supabase
      .from('produtos')
      .update(produtoData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return produtoDBToProduto(produtoAtualizado);
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
    throw error;
  }
}

// Deletar produto
export async function deleteProduto(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('produtos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Erro ao deletar produto:', error);
    throw error;
  }
}

// Buscar produtos por termo (para busca global)
export async function searchProdutos(term: string, empresaId: string): Promise<Produto[]> {
  try {
    const termLower = term.toLowerCase();
    
    const { data: produtos, error } = await supabase
      .from('produtos')
      .select('*')
      .eq('empresa_id', empresaId)
      .or(`codigo.ilike.%${term}%,descricao.ilike.%${termLower}%,categoria.ilike.%${termLower}%`)
      .limit(20);

    if (error) throw error;

    return (produtos || []).map(produtoDBToProduto);
  } catch (error) {
    console.error('Erro ao buscar produtos:', error);
    throw error;
  }
}

